/**
 * Overlays built on Radix primitives: dialogs, side drawers, menus,
 * popovers and tooltips, styled to the brand.
 */
import * as RDialog from "@radix-ui/react-dialog";
import * as RMenu from "@radix-ui/react-dropdown-menu";
import * as RPopover from "@radix-ui/react-popover";
import * as RTooltip from "@radix-ui/react-tooltip";
import { X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Button } from "./ui";

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = "md",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
}) {
  const w = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" }[width];
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-moss/35 backdrop-blur-[2px]" />
        <RDialog.Content
          className={cn(
            "fixed top-[8vh] left-1/2 z-50 flex max-h-[84vh] w-[calc(100vw-32px)] -translate-x-1/2 animate-pop-in flex-col rounded-2xl bg-paper shadow-[var(--shadow-pop)] focus:outline-none",
            w,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line-soft px-6 pt-5 pb-4">
            <div className="min-w-0">
              <RDialog.Title className="font-display text-xl font-semibold text-moss">{title}</RDialog.Title>
              {description ? (
                <RDialog.Description className="mt-0.5 text-sm text-muted">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{typeof title === "string" ? title : "Dialog"}</RDialog.Description>
              )}
            </div>
            <RDialog.Close className="-mr-2 grid size-8 place-items-center rounded-md text-muted hover:bg-sage-50 hover:text-moss" aria-label="Close">
              <X className="size-4" />
            </RDialog.Close>
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 rounded-b-2xl border-t border-line-soft bg-cream/50 px-6 py-3.5">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/** A panel that slides in from the right, for quick views and edits. */
export function Drawer({
  open,
  onOpenChange,
  title,
  subtitle,
  children,
  actions,
  width = 560,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  width?: number;
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-moss/25" />
        <RDialog.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full animate-slide-in flex-col bg-paper shadow-[var(--shadow-pop)] focus:outline-none"
          style={{ maxWidth: width }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-line-soft px-6 pt-5 pb-4">
            <div className="min-w-0">
              <RDialog.Title className="font-display text-xl font-semibold text-moss">{title}</RDialog.Title>
              <RDialog.Description asChild={!!subtitle} className={subtitle ? "mt-0.5 text-sm text-muted" : "sr-only"}>
                {subtitle ? <div>{subtitle}</div> : "Details"}
              </RDialog.Description>
            </div>
            <div className="flex items-center gap-1">
              {actions}
              <RDialog.Close className="grid size-8 place-items-center rounded-md text-muted hover:bg-sage-50 hover:text-moss" aria-label="Close">
                <X className="size-4" />
              </RDialog.Close>
            </div>
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

/** Ask before doing something that is hard to undo. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel = "Confirm",
  tone = "primary",
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  tone?: "primary" | "danger";
  onConfirm: () => void;
  loading?: boolean;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={tone === "danger" ? "danger" : "primary"} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-body">{children}</div>
    </Dialog>
  );
}

export interface MenuItem {
  label: ReactNode;
  icon?: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export function Menu({ trigger, items, align = "end" }: { trigger: ReactNode; items: (MenuItem | "separator")[]; align?: "start" | "end" }) {
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>{trigger}</RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align={align} sideOffset={6} className="z-50 min-w-48 animate-pop-in rounded-xl border border-line-soft bg-paper p-1 shadow-[var(--shadow-pop)]">
          {items.map((it, i) =>
            it === "separator" ? (
              <RMenu.Separator key={i} className="my-1 h-px bg-line-soft" />
            ) : (
              <RMenu.Item
                key={i}
                disabled={it.disabled}
                onSelect={it.onSelect}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[highlighted]:bg-sage-50",
                  it.danger ? "text-danger-ink" : "text-ink",
                )}
              >
                {it.icon && <span className="text-muted [&>svg]:size-4">{it.icon}</span>}
                {it.label}
              </RMenu.Item>
            ),
          )}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

export function Popover({
  trigger,
  children,
  align = "start",
  open,
  onOpenChange,
  className,
}: {
  trigger: ReactNode;
  children: ReactNode;
  align?: "start" | "center" | "end";
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
  className?: string;
}) {
  return (
    <RPopover.Root open={open} onOpenChange={onOpenChange}>
      <RPopover.Trigger asChild>{trigger}</RPopover.Trigger>
      <RPopover.Portal>
        <RPopover.Content
          align={align}
          sideOffset={6}
          className={cn("z-50 animate-pop-in rounded-xl border border-line-soft bg-paper p-3 shadow-[var(--shadow-pop)] focus:outline-none", className)}
        >
          {children}
        </RPopover.Content>
      </RPopover.Portal>
    </RPopover.Root>
  );
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <RTooltip.Provider delayDuration={250}>{children}</RTooltip.Provider>;
}

export function Tooltip({ content, children, side = "top" }: { content: ReactNode; children: ReactNode; side?: "top" | "bottom" | "left" | "right" }) {
  if (!content) return <>{children}</>;
  return (
    <RTooltip.Root>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content side={side} sideOffset={6} className="z-50 max-w-72 animate-fade-in rounded-md bg-moss px-2.5 py-1.5 text-xs leading-snug text-paper shadow-lg">
          {content}
          <RTooltip.Arrow className="fill-moss" />
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}

/** useState for a dialog's open flag plus the record it's about. */
export function useDialog<T = true>() {
  const [value, setValue] = useState<T | null>(null);
  return {
    open: value !== null,
    value,
    show: (v: T) => setValue(v),
    close: () => setValue(null),
    onOpenChange: (o: boolean) => {
      if (!o) setValue(null);
    },
  };
}
